import React, { useEffect, useRef, useState } from 'react'
import { useField, useFormikContext } from 'formik'
import { InputFieldContainer } from './styles'
import { BaseFieldProps } from './index'
import { useNewTranslation } from 'hooks'
import { UploadType } from 'services/api/uploads'
import { showSnackBar } from 'services/toast'
import { convertToBytes, parseFileSize } from 'particles/utils'
import { uploadFileToS3, getFileFormat, S3UploadError } from 'services/upload/s3Upload'
import { trackUpload } from 'services/upload/uploadTracker'
import FileUploader, { AcceptType, FileState, FileUploaderHandle } from 'particles/FileUploader'
import { LabelSize } from 'particles/Label'

interface FileError {
    inlineError: boolean
    message?: string
}

interface ImageUploadInputProps extends BaseFieldProps {
    name: string
    presetType?: UploadType
    maxFiles: number
    uploadContainerWidth?: string
    requiredWidth?: number
    requiredHeight?: number
    placeholder?: string
    identifier?: string
    isCommentable?: boolean
    isCommented?: boolean
    showError?: boolean // set false if want to use external error state to control the error display
    validateByTouch?: boolean // set to false if you want to use an external error state to control error display. It will show errors without requiring the field to be touched.
    dataTestId?: string
    maxWidth?: string
    labelSize?: LabelSize
    acceptTypes?: AcceptType[]
}

const ImageUploaderField: React.FC<ImageUploadInputProps> = ({
    presetType = UploadType.CAMPAIGN,
    maxFiles,
    title,
    subtitle,
    isRequired,
    uploadContainerWidth,
    requiredWidth,
    requiredHeight,
    placeholder,
    isDisable,
    isReadOnly = false,
    identifier,
    name,
    showError = true,
    validateByTouch = true,
    maxWidth,
    acceptTypes,
    isCommentable = false,
    isCommented = false,
    ...props
}) => {
    const [t] = useNewTranslation()

    const [field, meta] = useField<any>(name)
    const { setFieldValue, setFieldTouched, isSubmitting, setSubmitting } = useFormikContext()
    const fieldValue = field.value || []
    const hasError = showError && !!meta.error && (!validateByTouch || meta.touched)
    const [uploadProgressMap, setUploadProgressMap] = useState<Record<string, number>>({})

    const [isLoading, setIsLoading] = useState<boolean>(false)
    const [errors, setErrors] = useState<(FileError | undefined)[]>([])
    const [uploadingFiles, setUploadingFiles] = useState<FileState[]>([])
    const uploaderRef = useRef<FileUploaderHandle>(null)
    // one controller per in-flight file so the remove button can cancel just that upload
    const abortControllersRef = useRef(new Map<File, AbortController>())

    // cancel uploads that are still running when the field goes away (modal closed, page left),
    // otherwise they finish into an unmounted field and the S3 object is never referenced
    useEffect(() => {
        const abortControllers = abortControllersRef.current
        return () => abortControllers.forEach(controller => controller.abort())
    }, [])

    useEffect(() => {
        if (isSubmitting) {
            uploaderRef?.current?.resetFileErrors()
        }
    }, [isSubmitting])

    const addItems = (data: any): void => {
        const items = fieldValue
        setFieldValue(field.name as never, items.concat(data))
    }

    const handleRemoveFile = async (file: FileState, index: number) => {
        // still uploading: `index` counts past the uploaded items, so it can't be spliced out
        // of the field value. Cancelling makes uploadFileToS3 abort the multipart upload
        // server-side and settle as 'aborted', which never reaches the field value.
        if (uploadingFiles.includes(file)) {
            abortControllersRef.current.get(file)?.abort()
            setUploadingFiles(prev => prev.filter(uploadingFile => uploadingFile !== file))
            return
        }

        const items = [...fieldValue]
        items.splice(index, 1)
        setFieldValue(field.name as never, items)
        if (items.length === 0) setFieldTouched(field.name as never, true, false)
    }

    const handleSubmit = (responses: any[], batchFiles: FileState[]): void => {
        const uploadData = responses.map((response: any) => response.data)
        // only this batch: another batch dropped in meanwhile may still be uploading
        setUploadingFiles(prev => prev.filter(file => !batchFiles.includes(file)))
        // skip when nothing succeeded (e.g. all removed or cancelled on unmount), so a stale
        // `fieldValue` from when the upload started doesn't overwrite newer edits
        if (uploadData.length) {
            addItems(uploadData)
        }
        setSubmitting(false)
    }

    const imageLimit = Number(process.env.REACT_APP_UPLOAD_IMAGE_LIMIT ? process.env.REACT_APP_UPLOAD_IMAGE_LIMIT : 10)
    const videoLimit = Number(process.env.REACT_APP_UPLOAD_VIDEO_LIMIT ? process.env.REACT_APP_UPLOAD_VIDEO_LIMIT : 100)
    const FILE_SIZE_TOO_LARGE_ERROR: FileError = {
        inlineError: true,
        message: t('errorMessage.general.uploadSmallerPhotosVideos', {
            imageLimit: parseFileSize(imageLimit),
            videoLimit: parseFileSize(videoLimit),
        }),
    }

    type FileOutcome = { status: 'success'; data: any } | { status: 'error'; error?: FileError } | { status: 'aborted' }

    const uploadViaS3 = (files: FileState[]): void => {
        setIsLoading(true)
        setUploadingFiles(prev => [...prev, ...files])
        setSubmitting(true)
        const releaseUploadTracking = trackUpload()

        // index-aligned with `files`; pre-filled for oversized files that are rejected before
        // ever calling initiate, then filled in as each network upload settles
        const outcomes: (FileOutcome | undefined)[] = new Array(files.length).fill(undefined)

        let hasOversizedFile = false
        files.forEach((file: File, index: number) => {
            const isVideo = file.type.startsWith('video/')
            const limitBytes = convertToBytes(isVideo ? videoLimit : imageLimit)
            if (file.size > limitBytes) {
                outcomes[index] = { status: 'error', error: FILE_SIZE_TOO_LARGE_ERROR }
                hasOversizedFile = true
            }
        })

        // the Cloudinary path surfaces this as a toast when the server rejects an oversized
        // file; rejecting client-side must not silently drop that feedback
        if (hasOversizedFile) {
            showSnackBar({
                title: t('errorMessage.validation.photoVideoTooLargeTitle'),
                type: 'error',
            })
        }

        const filesToUpload = files
            .map((file: File, index: number) => ({ file, index }))
            .filter(({ index }) => outcomes[index] === undefined)

        const uploadPromises = filesToUpload.map(({ file }) => {
            const controller = new AbortController()
            abortControllersRef.current.set(file, controller)
            return uploadFileToS3(file, presetType, {
                signal: controller.signal,
                onProgress: progress => {
                    setUploadProgressMap(prev => ({
                        ...prev,
                        [file.name]: progress,
                    }))
                },
            })
        })

        Promise.allSettled(uploadPromises).then(results => {
            // the rest of this callback runs synchronously, so navigation can't slip in before
            // the results reach the form
            releaseUploadTracking()
            results.forEach((result, resultIndex) => {
                const { file, index } = filesToUpload[resultIndex]
                const isRemovedByUser = abortControllersRef.current.get(file)?.signal.aborted ?? false
                abortControllersRef.current.delete(file)

                // removed after the complete request was already sent: the upload still
                // succeeded, but the user no longer wants it in the field
                if (result.status === 'fulfilled' && isRemovedByUser) {
                    outcomes[index] = { status: 'aborted' }
                    return
                }

                if (result.status === 'fulfilled') {
                    const data = result.value
                    outcomes[index] = {
                        status: 'success',
                        data: {
                            ...data,
                            file_format: data.file_format || getFileFormat(file),
                        },
                    }
                    return
                }

                const error = result.reason
                const kind: S3UploadError['kind'] = error instanceof S3UploadError ? error.kind : 'unknown'

                switch (kind) {
                    case 'network':
                        showSnackBar({
                            title: t('errorMessage.general.cantConnectToServerSubtitle'),
                            type: 'error',
                        })
                        outcomes[index] = { status: 'error', error: { inlineError: false } }
                        break
                    case 'server':
                        showSnackBar({
                            title: t('errorMessage.general.cantUploadFileTitle'),
                            type: 'error',
                        })
                        outcomes[index] = { status: 'error', error: { inlineError: false } }
                        break
                    case 'validation':
                        showSnackBar({
                            title: t('errorMessage.validation.photoVideoTooLargeTitle'),
                            type: 'error',
                        })
                        outcomes[index] = { status: 'error', error: FILE_SIZE_TOO_LARGE_ERROR }
                        break
                    case 'aborted':
                        // user-initiated, no toast or inline error
                        outcomes[index] = { status: 'aborted' }
                        break
                    default:
                        showSnackBar({
                            title: t('errorMessage.general.unknownError'),
                            type: 'error',
                        })
                        outcomes[index] = { status: 'error', error: { inlineError: false } }
                }
            })

            const successResponses = outcomes
                .filter(
                    (outcome): outcome is Extract<FileOutcome, { status: 'success' }> => outcome?.status === 'success',
                )
                .map(outcome => ({ data: outcome.data }))
            const errorMessages = outcomes
                .filter((outcome): outcome is Extract<FileOutcome, { status: 'error' }> => outcome?.status === 'error')
                .map(outcome => outcome.error)

            setErrors(errorMessages)
            setIsLoading(false)
            handleSubmit(successResponses, files)
        })
    }

    const handleUpload = (files: FileState[]): void => {
        uploadViaS3(files)
    }

    const defaultTypes: AcceptType[] = ['image']

    if (presetType !== UploadType.BRAND) {
        defaultTypes.push('video')
    }

    return (
        <InputFieldContainer maxWidth={maxWidth}>
            <FileUploader
                ref={uploaderRef}
                fileUploadProgress={uploadingFiles.length ? uploadProgressMap : undefined}
                label={title}
                subtext={subtitle}
                maxFiles={maxFiles}
                placeholder={placeholder}
                uploadContainerWidth={uploadContainerWidth}
                isDisabled={isDisable}
                acceptTypes={acceptTypes ?? defaultTypes}
                onUpload={handleUpload}
                onRemoveUploadedFile={handleRemoveFile}
                uploadedFiles={fieldValue}
                uploadingFiles={uploadingFiles}
                isLoading={isLoading}
                errorMessage={hasError ? meta.error : ''}
                selectedFilesErrors={errors}
                requiredHeight={requiredHeight}
                requiredWidth={requiredWidth}
                identifier={identifier}
                isReadOnly={isReadOnly}
                isRequired={isRequired}
                isCommentable={isCommentable}
                isCommented={isCommented}
                {...props}
            />
        </InputFieldContainer>
    )
}

export default ImageUploaderField
